import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { useTenant } from '../context/TenantContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart3, Eye, Users, TrendingUp, TrendingDown, Calendar,
  Smartphone, Monitor, Tablet, Lock, Zap, ArrowRight, RefreshCw,
  PieChart, Clock, ExternalLink
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart as RechartsPieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';

const Analytics = () => {
  const { t } = useTranslation();
  const { token, API } = useContext(AuthContext);
  const { tenant, isFeatureEnabled } = useTenant();
  const navigate = useNavigate();
  
  const [overview, setOverview] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [devices, setDevices] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(30);

  const hasAdvancedAnalytics = isFeatureEnabled('advancedAnalytics');

  useEffect(() => {
    fetchOverview();
    if (hasAdvancedAnalytics) {
      fetchDetailedAnalytics();
    }
  }, [dateRange, hasAdvancedAnalytics]);

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/analytics/overview?days=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(res.data);
    } catch (err) {
      console.error('Error fetching analytics overview:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedAnalytics = async () => {
    try {
      const [dailyRes, hourlyRes, itemsRes, categoriesRes, devicesRes, interactionsRes] = await Promise.all([
        axios.get(`${API}/analytics/daily?days=${dateRange}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/hourly?days=${Math.min(dateRange, 7)}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/top-items?days=${dateRange}&limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/top-categories?days=${dateRange}&limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/devices?days=${dateRange}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/interactions?days=${dateRange}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setDailyData(dailyRes.data.data);
      setHourlyData(hourlyRes.data.data);
      setTopItems(itemsRes.data.data);
      setTopCategories(categoriesRes.data.data);
      setDevices(devicesRes.data.data);
      setInteractions(interactionsRes.data.data);
    } catch (err) {
      console.error('Error fetching detailed analytics:', err);
    }
  };

  const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return <Smartphone className="w-5 h-5" />;
      case 'tablet': return <Tablet className="w-5 h-5" />;
      default: return <Monitor className="w-5 h-5" />;
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="analytics-page p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-purple-600" />
            {t('analytics.title', 'Menu Analytics')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('analytics.subtitle', 'Track how visitors interact with your public menu')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          >
            <option value={7}>{t('analytics.last7Days', 'Last 7 days')}</option>
            <option value={30}>{t('analytics.last30Days', 'Last 30 days')}</option>
            <option value={90}>{t('analytics.last90Days', 'Last 90 days')}</option>
          </select>
          <button
            onClick={() => { fetchOverview(); if (hasAdvancedAnalytics) fetchDetailedAnalytics(); }}
            className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Upgrade Banner for non-Pro users */}
      {!hasAdvancedAnalytics && (
        <div className="mb-6 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="bg-white/20 rounded-lg p-3">
                <Lock className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">
                  {t('analytics.unlockAdvanced', 'Unlock Advanced Analytics')}
                </h3>
                <p className="text-purple-50 mb-4">
                  {t('analytics.upgradeDescription', 'Upgrade to Pro to see detailed charts, hourly traffic patterns, top items, device breakdown, and more!')}
                </p>
                <button
                  onClick={() => navigate('/admin/upgrade')}
                  className="inline-flex items-center px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors shadow-md"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  {t('analytics.upgradeToPro', 'Upgrade to Pro')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overview Stats - Available to all */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Eye className="w-5 h-5 text-purple-600" />
            </div>
            {overview?.growth_percent !== 0 && (
              <span className={`flex items-center text-sm font-medium ${
                overview?.growth_percent > 0 ? 'text-green-500' : 'text-red-500'
              }`}>
                {overview?.growth_percent > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                {Math.abs(overview?.growth_percent || 0)}%
              </span>
            )}
          </div>
          <p className="text-2xl font-bold text-gray-800 dark:text-white">
            {formatNumber(overview?.total_views)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('analytics.totalViews', 'Total Page Views')}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
              <Users className="w-5 h-5 text-cyan-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800 dark:text-white">
            {formatNumber(overview?.unique_visitors)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('analytics.uniqueVisitors', 'Unique Visitors')}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800 dark:text-white">
            {formatNumber(overview?.today_views)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('analytics.todayViews', "Today's Views")}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Calendar className="w-5 h-5 text-orange-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800 dark:text-white">
            {formatNumber(overview?.yesterday_views)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('analytics.yesterdayViews', "Yesterday's Views")}
          </p>
        </div>
      </div>

      {/* Detailed Analytics - Pro Only */}
      {hasAdvancedAnalytics ? (
        <>
          {/* Daily Traffic Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
              {t('analytics.dailyTraffic', 'Daily Traffic')}
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyData}>
                  <defs>
                    <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getDate()}/${date.getMonth() + 1}`;
                    }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#8b5cf6" 
                    fillOpacity={1} 
                    fill="url(#colorViews)"
                    name={t('analytics.pageViews', 'Page Views')}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="unique_visitors" 
                    stroke="#06b6d4" 
                    fillOpacity={1} 
                    fill="url(#colorVisitors)"
                    name={t('analytics.visitors', 'Visitors')}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Hourly Distribution */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                {t('analytics.hourlyTraffic', 'Traffic by Hour')}
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="label" 
                      tick={{ fontSize: 10 }}
                      interval={2}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar 
                      dataKey="views" 
                      fill="#8b5cf6" 
                      radius={[4, 4, 0, 0]}
                      name={t('analytics.views', 'Views')}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Device Breakdown */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-cyan-600" />
                {t('analytics.deviceBreakdown', 'Device Breakdown')}
              </h3>
              <div className="h-64 flex items-center">
                {devices.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={devices}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="count"
                        nameKey="device"
                        label={({ device, percent }) => `${device} ${(percent * 100).toFixed(0)}%`}
                      >
                        {devices.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="w-full text-center text-gray-500 dark:text-gray-400">
                    {t('analytics.noDeviceData', 'No device data available yet')}
                  </div>
                )}
              </div>
              {devices.length > 0 && (
                <div className="flex justify-center gap-6 mt-4">
                  {devices.map((device, index) => (
                    <div key={device.device} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400 capitalize flex items-center gap-1">
                        {getDeviceIcon(device.device)}
                        {device.device}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Top Items and Categories */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Top Menu Items */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                {t('analytics.topItems', 'Top Menu Items')}
              </h3>
              {topItems.length > 0 ? (
                <div className="space-y-3">
                  {topItems.map((item, index) => {
                    const maxViews = topItems[0]?.views || 1;
                    const percentage = (item.views / maxViews) * 100;
                    return (
                      <div key={item.item_id} className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-6">
                          #{index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                              {item.name}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {formatNumber(item.views)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-purple-500 h-2 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {t('analytics.noItemData', 'No item data available yet')}
                </div>
              )}
            </div>

            {/* Top Categories */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                {t('analytics.topCategories', 'Top Categories')}
              </h3>
              {topCategories.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topCategories} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        tick={{ fontSize: 12 }} 
                        width={100}
                      />
                      <Tooltip />
                      <Bar 
                        dataKey="views" 
                        fill="#06b6d4" 
                        radius={[0, 4, 4, 0]}
                        name={t('analytics.views', 'Views')}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {t('analytics.noCategoryData', 'No category data available yet')}
                </div>
              )}
            </div>
          </div>

          {/* Interactions */}
          {interactions.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                {t('analytics.userInteractions', 'User Interactions')}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {interactions.map((interaction) => (
                  <div 
                    key={interaction.action}
                    className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center"
                  >
                    <p className="text-2xl font-bold text-gray-800 dark:text-white">
                      {formatNumber(interaction.count)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {interaction.action.replace(/_/g, ' ')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        /* Blurred Preview for non-Pro users */
        <div className="relative">
          <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
            <div className="text-center p-8">
              <Lock className="w-12 h-12 text-purple-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                {t('analytics.proFeature', 'Pro Feature')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('analytics.upgradeToSee', 'Upgrade to Pro to unlock detailed analytics')}
              </p>
              <button
                onClick={() => navigate('/admin/upgrade')}
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                <Zap className="w-5 h-5 mr-2" />
                {t('analytics.upgradeToPro', 'Upgrade to Pro')}
              </button>
            </div>
          </div>
          
          {/* Placeholder Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 opacity-50 pointer-events-none">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 h-72">
              <div className="h-full flex items-center justify-center">
                <BarChart3 className="w-16 h-16 text-gray-300 dark:text-gray-600" />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 h-72">
              <div className="h-full flex items-center justify-center">
                <PieChart className="w-16 h-16 text-gray-300 dark:text-gray-600" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
